import Button from '@mui/material/Button'
import { zonedDate } from 'global/helpers/dates'
import { type ReactElement, type ReactNode, useState } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import AutomaticStatusChip from 'screens/TripDetails/AutomaticStatusChip'
import { PrincipalId, type StopStatus } from './TripDetails.types'

export interface StatusHistoryItemProps {
  status: StopStatus
  withRemarks?: boolean
}

export interface StatusHistoryProps {
  statuses: StopStatus[]
  withRemarks?: boolean
}

const StatusHistory = ({
  statuses,
  withRemarks,
}: StatusHistoryProps): ReactNode => {
  const { t } = useTranslation()
  const [previousStatuses] = useState<StopStatus[]>(statuses.slice(1))
  const [showMore, setShowMore] = useState(false)

  if (statuses.length === 0) {
    return null
  }

  return (
    <Stack>
      <StatusHistoryItem status={statuses[0]!} withRemarks={withRemarks} />
      {showMore && previousStatuses.map((status) => (
        <StatusHistoryItem key={status.id} status={status} withRemarks={withRemarks} />
      ))}
      {previousStatuses.length > 0 && (
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={() => setShowMore(!showMore)}
          sx={{
            padding: 0,
            minHeight: 'auto',
            alignSelf: 'start',
          }}
        >
          {showMore ? t('general.show_less') : t('general.show_more')}
        </Button>
      )}
    </Stack>
  )
}

const StatusHistoryItem = ({
  status,
  withRemarks,
}: StatusHistoryItemProps): ReactElement => {
  const { t } = useTranslation()

  const isAutomaticallyUpdated = status.principalId === PrincipalId.SYSTEM

  return (
    <Stack>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Typography variant="p3" color="primary.main">
          {t(`waypoints.statuses.${status.id}`)}
          {isAutomaticallyUpdated && <AutomaticStatusChip sx={{
            marginLeft: 1,
            height: 20,
            padding: '0 4px',
            '& .MuiChip-label': {
              padding: 0,
            },
          }} />}
        </Typography>
        <Typography variant="p5" color="primary.main">
          {zonedDate(status.createdAt, 'full')}
        </Typography>
      </Stack>
      {withRemarks && !isAutomaticallyUpdated && status.comment && (
        <Typography variant="p5" color="primary.main">
          {status.comment}
        </Typography>
      )}
    </Stack>
  )
}

export default StatusHistory
